// @flow
import React, {
    useState, useEffect, useContext, useRef,
} from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { connectAPI, withConfirmation } from 'speed-js-react';
import HorizontalLine from 'esm-web-pro/components/layout/HorizontalLine';
import MainActions from 'esm-web-pro/components/layout/sidePanel/MainActions';
import EditIcon from 'esm-web-pro/components/layout/sidePanel/icons/EditIcon';
import DeleteIcon from 'esm-web-pro/components/layout/sidePanel/icons/DeleteIcon';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import type { LeafletContext } from 'react-leaflet/lib/types';
import { withLeaflet } from 'react-leaflet';
import { withRouter } from 'react-router';
import type { RouterHistory, Match } from 'react-router-dom';
import type { Map } from 'esm-web-pro/containers/map/types';
import type { Coverage } from 'esm-web-pro/types/coverages';
import { Box } from 'grommet';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import UpperContainer from 'esm-web-pro/components/layout/sidePanel/UpperContainer';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import UseHighlightPolygon from 'esm-web-pro/hooks/useHighlightPolygon';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import type { Confirmation } from 'speed-js-react';
import BottomContainer from 'esm-web-pro/components/layout/sidePanel/BottomContainer';
import NavigateBackButton from 'esm-web-pro/components/maps/NavigateBackButton';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { MapContext } from 'esm-web-pro/containers/map/context';
import { SidePane } from 'esm-web-pro/components/layout';
import PolygonIcon from 'esm-web-pro/components/layout/sidePanel/icons/PolygonIcon';
import RegionAttributes from 'esm-web-pro/containers/pages/network/objects/region/RegionAttributes';
import MapObjectForm from '../../../../map/forms/MapObjectForm';
import ActionButtons from '../../../../../components/forms/ActionButtons';

type Props = {
    updateRegion: Function,
    object: Coverage,
    objectLayer: Object,
    intl: IntlShape,
    history: RouterHistory,
    match: Match,
    map: Map,
    editor: BaseEditor,
    leaflet: LeafletContext,
    deleteRegion: Function,
    confirm: (confirmation: Confirmation) => void,
}

const msg = defineMessages({
    deleteRegion: {
        id: 'delete-region-title',
        defaultMessage: 'Are you sure you want to delete the region?',
    },
    cancel: {
        id: 'cancel-delete-region',
        defaultMessage: 'Cancel',
    },
    confirm: {
        id: 'confirm-delete-region',
        defaultMessage: 'Confirm',
    },
});

const panelId = 'REGION_SIDE_PANEL_ID';

const Region = (props: Props) => {
    const {
        intl: { formatMessage },
        history,
        match,
        object,
        objectLayer,
        map: { is_draft: isDraft, uuid: mapUuid },
        editor,
        updateRegion,
        leaflet,
        deleteRegion,
    } = props;

    const { uuid } = useContext(MapContext);
    const formRef = useRef();
    const [editMode, setEditMode] = useState(false);
    const [updatedName, setUpdatedName] = useState('');

    const coordinates = object.polygon.coordinates[0].map((c) => [c[1], c[0]]);

    const handleFocus = () => {
        const highlightPolygon = UseHighlightPolygon(
            coordinates,
            [{
                weight: 6, className: 'polygon_line',
            },
            {
                weight: 2,
                dashArray: null,
                className: '',
            }],
        );
        editor.registerLoadingCallback(highlightPolygon);
        leaflet.map.highlightedGeometry = {
            bounds: coordinates, center: null,
        };
        leaflet.map.panInsideBounds(coordinates);
        editor.reloadFeatures();
    };

    const memoizedFocus = React.useCallback(handleFocus, [object]);

    useEffect(() => {
        memoizedFocus();
        return () => {
            editor.unregisterAllLoadingCallback();
        };
    }, [memoizedFocus, editor]);

    const navigateBack = () => {
        history.push(match.url.split('/layer')[0]);
    };

    const onDeleteRegion = () => {
        const { confirm } = props;
        confirm({
            message: formatMessage(msg.deleteRegion),
            actions: {
                cancel: {
                    label: formatMessage(msg.cancel),
                    callback: () => {},
                    type: 'negative',
                },
                confirm: {
                    label: formatMessage(msg.confirm),
                    callback: () => {
                        deleteRegion(mapUuid, object.uuid).then(navigateBack);
                    },
                    type: 'positive',
                },
            },
        });
    };

    const actions = [];
    if (isDraft) {
        actions.push({
            key: `${object.uuid}-edit`,
            icon: <EditIcon />,
            title: formatMessage(actionMessages.edit),
            callback: () => enableRegionUpdate(objectLayer),
            color: 'transparent',
        }, {
            key: `${object.uuid}-delete`,
            icon: <DeleteIcon />,
            title: formatMessage(actionMessages.delete),
            callback: () => onDeleteRegion(),
            color: 'transparent',
        });
    }

    const enableRegionUpdate = () => {
        setEditMode(true);
        if (objectLayer) {
            objectLayer.pm.enable({
                allowSelfIntersection: false,
            });
        }
    };

    const disableRegionUpdate = () => {
        setEditMode(false);
        objectLayer.pm.disable();
    };

    const onChange = (e) => {
        setUpdatedName(e.target.value);
    };

    const onSubmit = () => {
        const name = updatedName || object.name;
        const polygon = objectLayer.toGeoJSON().geometry;
        const data = {
            name,
            polygon,
        };
        updateRegion(uuid, object.uuid, data)
            .then((res) => {
                objectLayer.pm.disable();
                navigateBack();
            })
            .catch((err) => console.log(err));
    };

    return (
        <SidePane
            id={panelId}
            openInitially
            closeAllOpened
            continuity
            title={`${object.polygon.type} ${object.name}`}
            titleLabel={<FormattedMessage id="sidebar-network-region" defaultMessage="Region" />}
            titleIcon={<PolygonIcon />}
            actions={(isDraft && editMode) ? (
                <ActionButtons
                    onClose={disableRegionUpdate}
                    onSave={onSubmit}
                    disabled={false}
                />
            )
                : <BottomContainer actions={<NavigateBackButton panelId={panelId} />} />}
        >
            <Box height="100%" justify="between">
                <Box>
                    <UpperContainer>
                        <HorizontalLine color="grey" />
                        <RegionAttributes
                            region={object}
                        />
                        <HorizontalLine color="grey" />
                        <Box direction="row" pad={{ vertical: '15px' }}>
                            {!editMode && <MainActions main actions={actions} />}
                        </Box>
                        {(isDraft && editMode)
                     && (
                         <Box
                             height="100%"
                         >
                             <MapObjectForm
                                 key="network"
                                 resourceName="gisRegions.geo"
                                 excludeFields={['polygon']}
                                 resourceArguments={[uuid]}
                                 onChange={onChange}
                                 forwardRef={formRef}
                                 initial={{ name: object.name }}
                             />
                         </Box>
                     )
                        }
                        <HorizontalLine color="grey" />
                    </UpperContainer>
                </Box>
            </Box>
        </SidePane>
    );
};

// Coverage.defaultProps

const apiCallback = (apiClient) => ({
    deleteRegion: {
        send: (mapUuid, id) => apiClient.resources['gisRegions.geo'].delete(mapUuid, id),
    },
    updateRegion: {
        send: (map, objectId, data) => apiClient.resources['gisRegions.geo'].update(map, objectId, data),
        success: ((res) => res),
        error: ((err) => err),
    },
});

const mapDispatchToProps = (dispatch) => (
    bindActionCreators(sidePanelActions, dispatch)
);


export default connect(null, mapDispatchToProps)(
    connectAPI(apiCallback)(
        withLeaflet(
            injectIntl(
                withRouter(
                    withConfirmation(
                        Region,
                    ),
                ),
            ),
        ),
    ),
);
